import { forwardRef, useEffect, type KeyboardEvent, type MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  Lock as LockIcon,
  Mail,
  MailOpen,
  Pin,
  PinOff,
  Star,
  Tag,
  Unlock,
  Zap,
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { avatarGradientClass } from '@/lib/avatar-gradient';
import { formatPhone } from '@/lib/format-phone';
import { formatRelativeTime } from '@/lib/format-time';
import {
  CUCI_TAG,
  SPECIAL_TAG,
  type Conversation,
  type Lock,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';
import { useClaimConversation } from '@/features/conversations/useClaimConversation';
import { useConversationFlags } from '@/features/conversations/useConversationFlags';
import { useReleaseConversation } from '@/features/conversations/useReleaseConversation';
import { useReadConversation } from '@/features/conversations/useReadConversation';
import { useAuthStore } from '@/store/auth';
import { useUIStore } from '@/store/ui';

/** Identity badge naming an account — rendered on aggregate-mode ("所有账号")
 *  conversation rows (per-account rows carry no channel/identity badge).
 *  `label` is the account's display label (`formatAccountLabel`);
 *  `badgeClassName` is that account's tinted `AccountColor.badge` class string
 *  (see `account-colors.ts`). */
export interface AccountBadge {
  label: string;
  badgeClassName: string;
}

interface ConversationRowProps {
  conversation: Conversation;
  /** Set this to expose the row at a given height to the virtualizer; the
   *  row itself just renders normally. Used by `useVirtualizer`'s
   *  `measureElement` hook in `ConversationList`. */
  measureRef?: (node: HTMLElement | null) => void;
  /** Optional inline style for the absolute-positioned virtualizer wrapper.
   *  When provided, the row renders as an absolutely-positioned child of
   *  the virtualizer's relative-positioned scroll container. */
  style?: React.CSSProperties;
  /** Index in the virtualized list; mirrored onto `data-index` so the
   *  virtualizer can find the node for measurement. */
  dataIndex?: number;
  /** Colored badge naming the account this conversation belongs to. Passed
   *  only by `ConversationList` in aggregate ("所有账号") mode; `undefined` in
   *  per-account mode and also when the account isn't in the accounts cache
   *  yet — in both cases the row simply shows no identity badge (see
   *  `ConversationList`'s `accountBadgeById`). */
  accountBadge?: AccountBadge;
}

/**
 * One row in the middle-column conversation list.
 *
 * Layout (left -> right, top -> bottom):
 *   1. Avatar (initials of contact display_name or phone)
 *   2. A text block, three stacked rows:
 *        Row 1: phone (primary — the operational identifier agents care
 *          about in this gaming-support console; falls back to the display
 *          name, then the peer-id, when there's no phone) — timestamp for
 *          this row lives in the right-hand column (3), not here.
 *        Row 2: contact display name (muted, left — only when both a phone
 *          AND a non-blank name exist, otherwise the left slot is empty) and,
 *          right-aligned opposite it, the account identity badge — a colored
 *          badge naming the conversation's account, shown ONLY in the aggregate
 *          "所有账号" view (`accountBadge`); per-account rows carry no badge
 *          (the old "WA" channel badge was removed). Row 2 renders when there's
 *          a name, a 要cuci pill, a Special pill, or that badge — see
 *          `showRow2`.
 *        Row 3: message preview text.
 *   3. Relative timestamp (top, with a 📌 pin icon when pinned), unread badge
 *      (count when unread_count > 0, or a numberless attention dot when only
 *      the manual marked_unread flag is set), lock pill (if locked), action
 *      buttons (revealed on hover, focus, or when this row is selected)
 *
 * Right-clicking the card opens a context menu with the four GLOBAL flag
 * actions (shared desk — every console sees the result): 置顶/取消置顶 (pin),
 * 标记未读/清除未读标记 (manual attention flag), 标记要cuci/取消要cuci and
 * 标记Special/取消Special (the two business tags). Labels toggle by current
 * state; each item fires one `useConversationFlags` mutation. When tagged
 * 要cuci an amber inset pill renders in row 2's right-hand group; when
 * tagged Special a violet variant of the same pill renders beside it.
 * Pinned ordering comes from the SERVER (`pinned_at DESC NULLS LAST` first)
 * — the row only renders state.
 *
 * The lock state is fetched lazily via `['conversations', 'lock', id]` —
 * the backend's `ConversationOut` does not embed it, and there is no batch
 * endpoint (see backend contract notes in conversations.api.ts).
 */
export const ConversationRow = forwardRef<HTMLDivElement, ConversationRowProps>(
  ({ conversation, measureRef, style, dataIndex, accountBadge }, _ref) => {
    // forwardRef requires the second arg; we don't currently pipe it through
    // because the virtualizer uses `measureRef` for measurement instead.
    void _ref;
    const selectedConversationId = useUIStore((s) => s.selectedConversationId);
    const setSelectedConversationId = useUIStore(
      (s) => s.setSelectedConversationId,
    );
    const currentAgent = useAuthStore((s) => s.agent);
    const isSelected = selectedConversationId === conversation.id;

    // M5-T6: cache-only read. The lock cache slot is fed by THREE sources
    // and we deliberately do NOT issue a network probe here:
    //   1. the claim/release mutation `onSuccess` (the user we just acted on),
    //   2. the 409-conflict body seeded by `useClaimConversation` (we know who
    //      holds it the moment we tried to take it),
    //   3. the WebSocket `conversation.lock` event projection (any other
    //      tab — including a different agent's tab — claiming or releasing).
    //
    // Trade-off documented for the architect: a hard browser reload while
    // another agent already holds a lock paints this row as unlocked until
    // either the user issues their own claim (which 409s and seeds the cache
    // with truth) or a `conversation.lock` event fires (which only happens
    // on the next mutation by anyone). Acceptable because the lock is a
    // 5-minute soft lock — the real holder will renew on their own activity,
    // which republishes the event. The `ChatPanel.tsx` header lock pill
    // shares the exact same query key + `enabled: false` so the trade-off
    // is uniform across the row and the chat header.
    const lockQuery = useQuery<Lock | null, Error>({
      queryKey: conversationsKeys.lock(conversation.id),
      queryFn: () => Promise.resolve(null),
      enabled: false,
    });

    const claim = useClaimConversation();
    const release = useReleaseConversation();
    const markRead = useReadConversation();
    const flags = useConversationFlags();

    // The global flags this row renders / toggles. `needsAttention` unifies
    // "has unread messages" with "manually marked unread" — the two states
    // share the attention affordances (badge cluster + Mark-read action)
    // and the backend's read endpoint clears both together.
    const isPinned = conversation.pinned_at !== null;
    const hasCuci = conversation.tags.includes(CUCI_TAG);
    const hasSpecial = conversation.tags.includes(SPECIAL_TAG);
    const needsAttention =
      conversation.unread_count > 0 || conversation.marked_unread;

    // Auto-mark-read when this row becomes the selected row. Cheap to dispatch
    // (the mutation is idempotent server-side); the optimistic patch makes
    // the badge update instant in the cache slot. Also fires when only the
    // manual marked_unread flag is set — viewing the conversation IS the
    // attention it asked for, and the backend read endpoint clears the flag.
    useEffect(() => {
      if (isSelected && needsAttention) {
        markRead.mutate({ conversationId: conversation.id });
      }
      // We deliberately don't depend on `markRead` (its identity changes per
      // render) — `useMutation` returns a stable enough surface in practice
      // and re-running the effect would loop with the unread_count update.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, conversation.id, needsAttention]);

    const lock = lockQuery.data ?? undefined;
    const isExpired = lock ? new Date(lock.expires_at).getTime() < Date.now() : false;
    const effectiveLock = lock && !isExpired ? lock : undefined;
    const isLockedBySelf =
      effectiveLock && currentAgent
        ? effectiveLock.locked_by_agent_id === currentAgent.id
        : false;
    const isLockedByOther = effectiveLock !== undefined && !isLockedBySelf;

    // Initials always prefer the display name (falling back as below) —
    // kept independent of the primary/secondary text below so a digits-only
    // primary line (the phone) never regresses the avatar to number initials.
    const contactName =
      conversation.contact.display_name?.trim() ||
      conversation.contact.phone_e164 ||
      conversation.contact.peer_id;
    const initials = computeInitials(contactName);

    // PRIMARY line: the customer's phone number is the operational
    // identifier agents care about in this gaming-support console, so it
    // takes precedence over the WhatsApp display name; falls back to
    // `contactName` (display name, then peer-id) when there's no phone.
    const hasPhone = !!conversation.contact.phone_e164;
    const primaryText = hasPhone
      ? formatPhone(conversation.contact.phone_e164)
      : contactName;
    // Row 2 LEFT slot: the display name, shown only when the primary line is
    // showing the phone (there IS a phone) AND a non-blank display name also
    // exists — mirrors the old `showPhoneRow` condition with the two roles
    // swapped; avoids duplicating the primary line. When this is false, row 2
    // still renders (see `showRow2` below) with an empty left slot and the
    // badge alone on the right.
    const showNameRow =
      hasPhone && !!conversation.contact.display_name?.trim();

    // Row 2 RIGHT slot: the account identity badge. Aggregate ("所有账号") mode
    // renders an account-colored badge naming this conversation's account;
    // per-account mode renders NOTHING (`null`). The old cyan "WA" WhatsApp
    // channel badge was removed — every conversation in this console is
    // WhatsApp, so a per-row "WA" stamp was pure noise. Computed once here
    // (rather than inline in the JSX) so `showRow2` can check its presence
    // without duplicating the ternary.
    const badgeNode = accountBadge ? (
      // Neumorphic: the account tag is the design's INSET micro-pill —
      // 9.5px/700, 2px 8px padding, 8px radius, account color as TEXT color
      // (`badgeClassName` is now text-only — see account-colors.ts).
      <Badge
        variant="outline"
        className={cn(
          'max-w-[110px] shrink-0 truncate rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none shadow-neu-inset-sm',
          accountBadge.badgeClassName,
        )}
      >
        {accountBadge.label}
      </Badge>
    ) : null;

    // Row 2 (name + right-slot group) renders whenever there's a name to show,
    // a business-tag pill (要cuci / Special) to show, OR an account badge to
    // show. Now that per-account mode carries no channel/identity badge,
    // `hasCuci` / `hasSpecial` MUST be part of this — otherwise a tagged
    // conversation with no display name and no account badge (i.e.
    // per-account mode) would silently drop its pill along with the row.
    const showRow2 = showNameRow || hasCuci || hasSpecial || Boolean(badgeNode);

    function handleSelect(): void {
      setSelectedConversationId(conversation.id);
    }

    function handleRowKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect();
      }
    }

    function handleClaim(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      claim.mutate({ conversationId: conversation.id });
    }

    function handleForceClaim(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      claim.mutate({ conversationId: conversation.id, force: true });
    }

    function handleRelease(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      release.mutate({ conversationId: conversation.id });
    }

    function handleRead(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      markRead.mutate({ conversationId: conversation.id });
    }

    // --- Context-menu flag actions (GLOBAL — shared-desk semantics) --------
    // Each toggles exactly one flag; the PATCH body carries only that field.
    // Radix fires `onSelect` on menu-item pick; the menu content is portaled
    // so a pick never bubbles into the row's own onClick selection handler.

    function handleTogglePin(): void {
      flags.mutate({
        conversationId: conversation.id,
        patch: { pinned: !isPinned },
      });
    }

    function handleToggleMarkedUnread(): void {
      flags.mutate({
        conversationId: conversation.id,
        patch: { marked_unread: !conversation.marked_unread },
      });
    }

    function handleToggleCuci(): void {
      // The tags field REPLACES the whole array server-side, so the toggle
      // sends the full desired set: current tags with 要cuci added/removed.
      // Unknown future tags (Phase-2 risk labels) are preserved untouched.
      const nextTags = hasCuci
        ? conversation.tags.filter((tag) => tag !== CUCI_TAG)
        : [...conversation.tags, CUCI_TAG];
      flags.mutate({
        conversationId: conversation.id,
        patch: { tags: nextTags },
      });
    }

    function handleToggleSpecial(): void {
      // Same full-array replacement discipline as the cuci toggle — only the
      // Special membership changes; every other tag survives verbatim.
      const nextTags = hasSpecial
        ? conversation.tags.filter((tag) => tag !== SPECIAL_TAG)
        : [...conversation.tags, SPECIAL_TAG];
      flags.mutate({
        conversationId: conversation.id,
        patch: { tags: nextTags },
      });
    }

    // Compose the action button cluster based on lock state. The cluster is
    // always rendered (so it's keyboard-reachable via Tab) but hidden via
    // `opacity-0` and revealed via `group-hover` / `group-focus-within` /
    // `aria-current="true"`. This is the affordance pattern the spec calls
    // for; keeping it in the DOM (not unmounted) avoids a layout shift when
    // the user hovers.

    return (
      // OUTER wrapper: owns the virtualizer contract (measureRef / style /
      // data-index) plus the visual gutter between cards. The gap must be
      // PADDING here — `measureElement` reads getBoundingClientRect().height,
      // which excludes margins, so a margin-based gap would make cards
      // overlap. INNER div: the interactive raised card (unchanged role /
      // handlers / aria — this split is visual structure only), wrapped in
      // the flags ContextMenu (same vendored Radix primitives as
      // MessageBubble's menu) — right-click opens it, left-click still
      // selects the row as before.
      <div
        ref={measureRef}
        data-index={dataIndex}
        style={style}
        className="px-4 py-1.5"
      >
        <ContextMenu>
          <ContextMenuTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          onClick={handleSelect}
          onKeyDown={handleRowKeyDown}
          aria-pressed={isSelected}
          className={cn(
            'neu-pressable group flex w-full cursor-pointer items-center gap-3 rounded-2xl border-l-[3px] border-transparent px-3 py-3 text-left text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            !isSelected &&
              'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
            isSelected && 'bg-[var(--bsel)] shadow-neu-selected',
          )}
        >
        <Avatar className="size-10 shrink-0 shadow-neu-flat">
          <AvatarFallback
            className={cn(
              'text-[13px] font-extrabold text-white',
              avatarGradientClass(conversation.contact.id),
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'min-w-0 truncate text-[13.5px] font-extrabold',
                hasPhone && 'tabular-nums',
                isSelected && 'text-[hsl(10,80%,50%)]',
              )}
            >
              {primaryText}
            </span>
          </div>
          {showRow2 && (
            <div className="flex items-center justify-between gap-2">
              {showNameRow ? (
                <p className="min-w-0 flex-1 truncate text-xs font-semibold text-muted-foreground">
                  {conversation.contact.display_name?.trim()}
                </p>
              ) : (
                // Empty spacer so `justify-between` still has two flex
                // children to split space between — with only `badgeNode`
                // as a lone child, `space-between` degrades to `flex-start`
                // per spec (a single flex item is flush with the start), so
                // the badge would sit flush LEFT instead of right without
                // this.
                <span aria-hidden="true" />
              )}
              {/* Right slot: the business-tag pills (要cuci amber / Special
                  violet, when tagged) grouped with the channel badge in one
                  flex container, so `justify-between` still sees exactly two
                  children (left slot + this group) and everything stays
                  right-aligned. Both pills mirror the account badge's
                  neumorphic inset-micro-pill styling; only the tint differs
                  (amber = cuci, violet = Special). */}
              <div className="flex shrink-0 items-center gap-1">
                {hasCuci && (
                  <Badge
                    variant="outline"
                    data-testid="cuci-pill"
                    className="shrink-0 rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none bg-[linear-gradient(145deg,#f5cf4e,#e5b93a)] text-[#7a5c00] shadow-[2px_2px_4px_var(--neu-dark-mid)]"
                  >
                    {CUCI_TAG}
                  </Badge>
                )}
                {hasSpecial && (
                  <Badge
                    variant="outline"
                    data-testid="special-pill"
                    className="shrink-0 rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none bg-[linear-gradient(145deg,#c9b8f5,#ab93e8)] text-[#3b1d7a] shadow-[2px_2px_4px_var(--neu-dark-mid)]"
                  >
                    {SPECIAL_TAG}
                  </Badge>
                )}
                {badgeNode}
              </div>
            </div>
          )}
          <p className="truncate text-xs font-semibold text-muted-foreground">
            {/* `last_message_preview` is the backend's display-ready summary:
                text body, or a server-side placeholder like "[Image]" /
                "[Voice]" for media without a caption, or null when there are
                no messages yet. */}
            {conversation.last_message_preview ?? <em>No messages yet</em>}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="flex items-center gap-1 text-[10.5px] font-bold text-neu-faint">
            {/* Pin indicator — sits just left of the timestamp so the pinned
                state reads at a glance without widening the row. */}
            {isPinned && (
              <Pin
                data-testid="pinned-icon"
                aria-label="pinned"
                className="size-3 shrink-0 text-primary"
              />
            )}
            {formatRelativeTime(conversation.last_message_at)}
          </span>
          <div className="flex items-center gap-1.5">
            {/* Attention badge: the numbered unread count wins when there
                are real unread messages; a numberless dot renders when only
                the manual marked_unread flag is set (same primary badge
                treatment — the row must read as needing attention either
                way, per the shared-desk flag semantics). */}
            {conversation.unread_count > 0 ? (
              <Badge
                variant="unread"
                className="h-5 min-w-[20px] justify-center rounded-full px-1.5 text-[10px] leading-none"
                aria-label={`${conversation.unread_count} unread`}
              >
                {conversation.unread_count}
              </Badge>
            ) : conversation.marked_unread ? (
              <Badge
                variant="unread"
                data-testid="marked-unread-dot"
                aria-label="marked unread"
                className="size-2.5 rounded-full p-0"
              />
            ) : null}
            {effectiveLock && (
              <LockPill lock={effectiveLock} isSelf={isLockedBySelf} />
            )}
          </div>

          {/* Action cluster. Hidden by default; revealed on hover, on focus,
              or when this row is the selected row. */}
          <div
            className={cn(
              'flex items-center gap-1 transition-opacity',
              'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
              isSelected && 'opacity-100',
            )}
          >
            {!effectiveLock && (
              <ActionButton
                label="Claim"
                onClick={handleClaim}
                disabled={claim.isPending}
              >
                <LockIcon className="size-3.5" />
              </ActionButton>
            )}
            {isLockedByOther && (
              <ActionButton
                label="Force claim"
                onClick={handleForceClaim}
                disabled={claim.isPending}
                variant="destructive"
              >
                <Zap className="size-3.5" />
              </ActionButton>
            )}
            {isLockedBySelf && (
              <ActionButton
                label="Release"
                onClick={handleRelease}
                disabled={release.isPending}
              >
                <Unlock className="size-3.5" />
              </ActionButton>
            )}
            {needsAttention && (
              <ActionButton
                label="Mark read"
                onClick={handleRead}
                disabled={markRead.isPending}
              >
                <Check className="size-3.5" />
              </ActionButton>
            )}
            {!needsAttention && !effectiveLock && (
              // Quiet placeholder so the cluster height is stable across
              // states. The Mark-read shortcut is the most-used action so
              // showing a no-op CheckCircle keeps the row visually steady.
              <span aria-hidden="true" className="text-muted-foreground/40">
                <CheckCircle2 className="size-3.5" />
              </span>
            )}
          </div>
        </div>
        </div>
          </ContextMenuTrigger>
          {/* The four GLOBAL flag actions. Labels toggle by current state so
              the menu always names the action it will perform, never the
              state it is in (置顶 pins; 取消置顶 unpins; etc.). */}
          <ContextMenuContent>
            <ContextMenuItem onSelect={handleTogglePin}>
              {isPinned ? <PinOff /> : <Pin />}
              {isPinned ? '取消置顶' : '置顶'}
            </ContextMenuItem>
            <ContextMenuItem onSelect={handleToggleMarkedUnread}>
              {conversation.marked_unread ? <MailOpen /> : <Mail />}
              {conversation.marked_unread ? '清除未读标记' : '标记未读'}
            </ContextMenuItem>
            <ContextMenuItem onSelect={handleToggleCuci}>
              <Tag />
              {hasCuci ? '取消要cuci' : '标记要cuci'}
            </ContextMenuItem>
            <ContextMenuItem onSelect={handleToggleSpecial}>
              <Star />
              {hasSpecial ? '取消Special' : '标记Special'}
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    );
  },
);
ConversationRow.displayName = 'ConversationRow';

// --- Sub-components --------------------------------------------------------

interface LockPillProps {
  lock: Lock;
  isSelf: boolean;
}

function LockPill({ lock, isSelf }: LockPillProps): React.JSX.Element {
  const label = isSelf
    ? `Locked by you`
    : `Locked by ${lock.locked_by_display_name || 'another agent'}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="status"
          aria-label={label}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
            isSelf
              ? 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
              : 'bg-amber-500/20 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300',
          )}
        >
          <LockIcon className="size-3" />
          <span className="max-w-[80px] truncate">
            {isSelf ? 'You' : lock.locked_by_display_name || 'Other'}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface ActionButtonProps {
  label: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  variant?: 'ghost' | 'destructive';
  children: React.ReactNode;
}

function ActionButton({
  label,
  onClick,
  disabled,
  variant = 'ghost',
  children,
}: ActionButtonProps): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant={variant}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className="size-6"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// --- Helpers ---------------------------------------------------------------

/** Two-letter initials. "John Smith" -> "JS"; "+60172671900" -> "+6"; empty
 *  fallback "??". */
function computeInitials(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '??';
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  const single = tokens[0];
  return single.slice(0, 2).toUpperCase();
}
